import { Alert } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { client } from '../../../client';

const ALERTS_KEY = ['alerts'];

export type AlertWithExecutions = Alert & {
  executions: { id: number; createdAt: Date; projectReportId: number }[];
};

export function useGetAlerts() {
  return useQuery({
    queryKey: ALERTS_KEY,
    queryFn: () =>
      client
        .get<AlertWithExecutions[]>('/alerts')
        .then((res: AxiosResponse<AlertWithExecutions[]>) => res.data),
  });
}

export function useCreateAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Alert, 'id' | 'workspaceId' | 'createdAt'>) =>
      client.post<Alert>('/alerts', data).then((res: AxiosResponse<Alert>) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });
}

export function useUpdateAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Alert> }) =>
      client.patch<Alert>(`/alerts/${id}`, data).then((res: AxiosResponse<Alert>) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      client.delete<Alert>(`/alerts/${id}`).then((res: AxiosResponse<Alert>) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });
}
